import { Feature, InjectorService } from '@tencent/workbench';
import { BaseFillFeature } from '@framework/workbench/drawer/ui/fill/base/base-fill-feature';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import {
    DirectOptions,
    LINEAR_GRADIENT, LinearDirectOptions,
    RADIAL_GRADIENT,
    RECT_GRADIENT, RectDirectOptions,
    SHAPE_GRADIENT, ShapeDirectOptions,
} from '@framework/workbench/drawer/ui/fill/constants';
import {
    GradientType,
} from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { ColorPickerType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import React from 'react';
import { IGradualFillShower } from '@tencent/drawing-drawer-components';
import { GradualFillFeature } from '@framework/workbench/drawer/ui/fill/gradual-fill/gradual-fill-feature';

@Feature()
export class GradualFillShower extends BaseFillFeature implements IGradualFillShower {
    private gradualFillFeature: GradualFillFeature;

    public constructor() {
        super();
        this.gradualFillFeature = InjectorService.get(GradualFillFeature);
    }

    public getColorPickerContainer = () => <ColorPickerContainer
        colorPickerType={ColorPickerType.StandardPC}
        onColorChange={this.gradualFillFeature.handlePaletteGradualColorChange}
        selectedTdColor={this.gradualFillFeature.getActivePoint()?.bgColor}
    />;

    public isLinearGradualFill(): boolean {
        return this.gradualFillFeature.value.curGradualType === LINEAR_GRADIENT;
    }

    public isRadialGradualFill(): boolean {
        return this.gradualFillFeature.value.curGradualType === RADIAL_GRADIENT;
    }

    public isRectGradualFill(): boolean {
        return this.gradualFillFeature.value.curGradualType === RECT_GRADIENT;
    }

    public isShapeGradualFill(): boolean {
        return this.gradualFillFeature.value.curGradualType === SHAPE_GRADIENT
            && !this.gradualFillFeature.value.shadeToTitle;
    }

    public isTitleGradualFill(): boolean {
        return !!(this.gradualFillFeature.value.curGradualType === SHAPE_GRADIENT
            && this.gradualFillFeature.value.shadeToTitle);
    }

    public getFillDirectionOptions() {
        const { curGradualType } = this.gradualFillFeature.value;
        if (curGradualType === LINEAR_GRADIENT) {
            return LinearDirectOptions;
        }
        if (curGradualType === RECT_GRADIENT) {
            return RectDirectOptions;
        }
        if (curGradualType === SHAPE_GRADIENT) {
            return ShapeDirectOptions;
        }
        return DirectOptions;
    }

    public getGradualFillOpacity() {
        const { alpha } = this.gradualFillFeature.value.activeGradualColor;
        const gradualOpacityObj = { gradualOpacity: alphaToOpacity(alpha) };
        return this.getShownValue(this.gradualFillFeature.getInputParams().key, gradualOpacityObj);
    }

    public getGradualFillAngle() {
        const gradualAngleObj = { gradualAngle: this.gradualFillFeature.value.gradualAngle };
        return this.getShownValue(this.gradualFillFeature.getInputParams().key, gradualAngleObj);
    }

    public shouldDirectionDisabled() {
        return this.gradualFillFeature.value.curGradualType === SHAPE_GRADIENT;
    }

    public shouldAngleDisabled() {
        return this.gradualFillFeature.value.curGradualType !== LINEAR_GRADIENT;
    }

    public getGradualPath = (): GradientType => {
        const { curGradualType } = this.gradualFillFeature.value;
        let path = GradientType.Linear;
        if (curGradualType === RADIAL_GRADIENT) {
            path = GradientType.Radial;
        } else if (curGradualType === RECT_GRADIENT) {
            path = GradientType.Rect;
        } else if (curGradualType === SHAPE_GRADIENT) {
            path = GradientType.Shape;
        }
        return path;
    };

    public getPointArray = () => this.gradualFillFeature.value.pointArray;

    public getCurrentDirection = () => this.gradualFillFeature.value.curDirect;

    public getGradualActivePointId = (): string => this.gradualFillFeature.value.activePointId;

    public getRotateWithShape = () => this.gradualFillFeature.value.rotateWithShape === 1;

    public shouldRotateWithShapeDisable = () => this.isBackground();
}
